<template>
    <div v-show="stepActive" class="tournament-participants">
        <h2 class="title">{{ $t('title_ParticipantSettings') }}</h2>
        <p class="subtitle">{{ $t('subtitle_ParticipantSettings') }}</p>

        <div class="info-card">
            <v-form>
                <div class="section">
                    <h3 class="section-title m-bttom d-flex align-center">
                        {{ $t('ranking.title') }}
                        <v-btn
                            icon
                            small
                            class="ml-2"
                            style="width: 0; height: 0; padding: 8px 8px; margin-top: 4px; background-color: #9a9a9a;"
                            @click="showRankingModal = true">
                            <v-icon style="font-size: 11px; font-weight: bold; color: white !important;">fa-question</v-icon>
                        </v-btn>
                    </h3>
                    <p class="subtitle-2 text--disabled mb-4 mt-1" style="margin-top: 0;">{{$t('ranking.description') }}</p>
                    <v-select
                        v-if="availableRankingTypes.length > 0"
                        v-model="rankingType"
                        :items="availableRankingTypes"
                        item-text="text"
                        item-value="value"
                        outlined
                        dense
                        append-icon="fa-regular fa-chevron-down"
                        @change="validateForm">
                    </v-select>
                    <v-text-field
                        v-else
                        :value="noRankingsMessage"
                        outlined
                        dense
                        readonly
                        disabled
                        :hint="$t('ranking.contactSupport') || 'Please contact Swiset support for assistance'"
                        persistent-hint>
                    </v-text-field>
                </div>
                <div class="section">
                    <h3 style="position: relative;" class="section-title mb-0 d-flex ">{{ $t('registration.title') }}<v-tooltip v-model="showTooltip" bottom
                            content-class="timeline-tooltip">
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn icon small class="ml-2" v-bind="attrs" v-on="on" style="width: 0; height: 0; padding: 8px 8px; margin-top: 4px; background-color: #9a9a9a;">
                                    <v-icon style="font-size: 11px; font-weight: bold; color: white !important;">fa-question</v-icon>
                                </v-btn>
                            </template>
                            <div class="timeline-tooltip-content">
                                <h4 class="tooltip-title mb-2">{{ $t('registration.tooltip.title') }}</h4>
                                <p class="tooltip-dates mb-4">
                                    {{ $t('registration.tooltip.duration', {
                                        start: formattedStartDate,
                                        end: formattedEndDate
                                    }) }}
                                </p>
                                <div class="timeline-container">
                                    <div class="timeline">
                                        <div class="timeline-base"></div>
                                        <div class="tournament-period" :style="{ width: '100%' }">
                                            <span class="period-label tournament">{{
                                                $t('registration.timeline.tournament')
                                                }}</span>
                                        </div>
                                        <template v-if="registrationPeriod !== 'NOT_ALLOWED'">
                                            <div class="registration-period"
                                                :style="{ width: `${getRegistrationWidth}%` }">
                                                <span class="period-label registration">{{
                                                    $t('registration.timeline.registration')
                                                    }}</span>
                                            </div>
                                        </template>
                                        <template v-else>
                                            <div class="registration-disabled">
                                                <span>{{ $t('registration.timeline.notAvailable') }}</span>
                                            </div>
                                        </template>
                                        <div class="timeline-markers">
                                            <div class="marker start">
                                                <div class="date">{{ formattedStartDate }}</div>
                                                <div class="label">{{ $t('registration.timeline.tournamentStart') }}
                                                </div>
                                            </div>
                                            <div v-if="registrationPeriod !== 'NOT_ALLOWED'"
                                                class="marker registration-end"
                                                :style="{ left: `${getRegistrationWidth}%` }">
                                                <div class="date registration-end-date">{{ formattedRegistrationEndDate
                                                    }}</div>
                                            </div>
                                            <div class="marker end">
                                                <div class="date end-date">{{ formattedEndDate }}</div>
                                                <div class="label">{{ $t('registration.timeline.tournamentEnd') }}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </v-tooltip></h3>
                    <p class="subtitle-2 text--disabled mb-4 mt-1" style="margin-top: 0;">{{
                        $t('registration.description') }}</p>
                    <div class="d-flex align-center">
                        <v-select v-model="registrationPeriod" :items="availableRegistrationPeriods" item-text="text"
                            item-value="value" outlined dense append-icon="fa-regular fa-chevron-down" class="flex-grow-1"
                            @change="validateForm"></v-select>
                    </div>
                </div>
                <div class="section mb-0">
                    <div>
                        <h3 class="section-title mb-0">{{ $t('inviteOnly.title') }}</h3>
                    </div>
                    <div class="d-flex justify-space-between">
                        <p class="subtitle-2 text--disabled mt-1">{{ $t('inviteOnly_ParticipantSettings.description') }}</p>
                        <v-switch v-model="inviteOnly" style="margin-top: -2px;"></v-switch>
                    </div>
                </div>
            </v-form>
        </div>

        <!-- Ranking Types Modal -->
        <v-dialog v-model="showRankingModal" max-width="900px" content-class="ranking-modal">
            <v-card>
                <v-card-title class="ranking-modal-header">
                    <h3>{{ $t('ranking.modal.title') }}</h3>
                    <v-btn icon @click="showRankingModal = false">
                        <v-icon>fa-times</v-icon>
                    </v-btn>
                </v-card-title>
                <v-card-text class="ranking-modal-content">
                    <p class="modal-description">{{ $t('ranking.modal.description') }}</p>
                    <div class="ranking-table-wrapper">
                        <table class="ranking-comparison-table">
                            <thead>
                                <tr>
                                    <th>{{ $t('ranking.modal.table.rankingType') }}</th>
                                    <th>{{ $t('ranking.modal.table.description') }}</th>
                                    <th>{{ $t('ranking.modal.table.formula') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="ranking in allRankingTooltips" :key="ranking.code">
                                    <td class="ranking-name">{{ ranking.title }}</td>
                                    <td class="ranking-description">{{ ranking.description }}</td>
                                    <td class="ranking-formula">
                                        <div v-if="typeof ranking.formula === 'string'" class="formula-single">
                                            {{ ranking.formula }}
                                        </div>
                                        <div v-else class="formula-dual">
                                            <div><strong>Demo:</strong> {{ ranking.formula.demo }}</div>
                                            <div><strong>Live:</strong> {{ ranking.formula.live }}</div>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex';
import TournamentRepository from '@/repositories/tournament';

export default {
    name: 'ParticipantSettings',
    props: {
        stepActive: {
            type: Boolean,
            required: true
        },
        setNextBtnDisabled: {
            type: Function,
            required: true
        },
        clubId: {
            type: Number,
            required: false
        }
    },
    data: () => ({
        showTooltip: false,
        showRankingModal: false,
        registrationPeriods: [
            { text: this?.$t('registration.periods.notAllowed'), value: 'NOT_ALLOWED' },
            { text: this?.$t('registration.periods.oneDay'), value: 'ONE_DAY' },
            { text: this?.$t('registration.periods.threeDays'), value: 'THREE_DAYS' },
            { text: this?.$t('registration.periods.fiveDays'), value: 'FIVE_DAYS' },
            { text: this?.$t('registration.periods.always'), value: 'ALWAYS' }

        ],
        availableRankings: [], // Start with empty array
        loading: false,
        noRankingsMessage: 'No ranking types available',
        // All possible ranking types
        allRankingCodes: [
            'SWISET_RATIO',
            'PROFIT_PERCENTAGE',
            'EQUITY_PERCENTAGE',
            'PROFIT_FACTOR',
            'AVG_WIN_LOSS_RATIO',
            'EQUITY_RT',
            'EQUITY_ROI_PERCENTAGE',
            'BALANCE',
            'PROFIT'
        ]
    }),
    computed: {
        ...mapState({
            formData: state => state.tournamentForm.participants,
            basicInfo: state => state.tournamentForm.basicInfo // Add this line
        }),
        // Account type is now determined by tournament type in TournamentDetails
        accountType() {
            // Get tournament type from details form
            const tournamentType = this.$store.state.tournamentForm.details.tournamentType;
            // Map tournament type to account type (DEMO stays DEMO, LIVE becomes LIVE)
            return tournamentType || 'ALL';
        },
        registrationPeriod: {
            get() { return this.formData.registrationPeriod; },
            set(value) {
                this.updateTournamentParticipants({
                    ...this.formData,
                    registrationPeriod: value
                });
            }
        },
        inviteOnly: {
            get() { return this.formData.inviteOnly; },
            set(value) {
                this.updateTournamentParticipants({
                    ...this.formData,
                    inviteOnly: value
                });
            }
        },
        rankingType: {
            get() { return this.formData.rankingType; },
            set(value) {
                this.updateTournamentParticipants({
                    ...this.formData,
                    rankingType: value
                });
            }
        },
        availableRankingTypes() {
            // Only use rankings from API
            if (this.availableRankings && this.availableRankings.length > 0) {
                return this.availableRankings.map(ranking => ({
                    text: ranking.name,
                    value: ranking.code
                }));
            }
            // If API returned empty or nothing loaded, show no options
            return [];
        },
        // Add new computed property
        tournamentDurationInDays() {
            if (!this.basicInfo.startDate || !this.basicInfo.endDate) return 0;
            const start = new Date(this.basicInfo.startDate);
            const end = new Date(this.basicInfo.endDate);
            return Math.ceil((end - start) / (1000 * 60 * 60 * 24));
        },
        // Modify availableRegistrationPeriods
        availableRegistrationPeriods() {
            return this.registrationPeriods.filter(period => {
                switch (period.value) {
                    case 'NOT_ALLOWED': return true;
                    case 'ONE_DAY': return this.tournamentDurationInDays >= 1;
                    case 'THREE_DAYS': return this.tournamentDurationInDays >= 3;
                    case 'FIVE_DAYS': return this.tournamentDurationInDays >= 5;
                    case 'ALWAYS': return true;            
                    default: return false;
                }
            });
        },
        formattedStartDate() {
            return this.basicInfo.startDate ? new Date(this.basicInfo.startDate).toLocaleDateString() : '';
        },
        formattedEndDate() {
            return this.basicInfo.endDate ? new Date(this.basicInfo.endDate).toLocaleDateString() : '';
        },
        formattedRegistrationEndDate() {
            if (!this.basicInfo.startDate || this.registrationPeriod === 'NOT_ALLOWED') return '';
            const start = new Date(this.basicInfo.startDate);
            const days = this.getRegistrationDays;
            const regEnd = new Date(start);
            regEnd.setDate(start.getDate() + days);
            return regEnd.toLocaleDateString();
        },
        getRegistrationDays() {
            switch (this.registrationPeriod) {
                case 'ONE_DAY': return 1;
                case 'THREE_DAYS': return 3;
                case 'FIVE_DAYS': return 5;
                case 'ALWAYS': return this.tournamentDurationInDays;
                default: return 0;
            }
        },
        getRegistrationWidth() {
            if (this.registrationPeriod === 'NOT_ALLOWED') return 0;
            return (this.getRegistrationDays / this.tournamentDurationInDays) * 100;
        },
        platformSelected() {
            const platform = this.$store.state.tournamentForm.details.platform;
            return platform && platform.version && platform.version !== 'None';
        },
        allRankingTooltips() {
            return this.allRankingCodes.map(code => {
                const tooltip = this.getRankingTooltip(code);
                return {
                    code,
                    ...tooltip
                };
            });
        }
    },
    watch: {
        rankingType: {
            handler(newVal, oldVal) {
                if (oldVal && newVal !== oldVal) {
                    // Reset rules when ranking type changes
                    this.updateTournamentRules([]);
                }
            }
        },
        // Remove platformSelected watcher as account type is now handled in TournamentDetails
    },
    methods: {
        ...mapMutations(['updateTournamentRules', 'updateTournamentParticipants']),

        getRankingTooltip(rankingCode) {
            const tooltipKey = `ranking.tooltips.${rankingCode}`;
            const tooltip = this.$t(tooltipKey);

            // Fallback in case translation doesn't exist
            if (tooltip === tooltipKey || !tooltip.title) {
                return {
                    title: rankingCode,
                    description: 'No description available',
                    formula: 'N/A'
                };
            }

            return tooltip;
        },

        async fetchAvailableRankings() {
            const platformInfo = this.$store.state.tournamentPlatformInfo;
            // Use selectedClubId from form state first (broker app selection), then clubMember, then prop
            const clubId = this.$store.state.tournamentForm?.basicInfo?.selectedClubId
                || this.$store.state?.clubMember?.club?.id
                || this.clubId;

            // Use either mtGroupId or platformGroupId, whichever is available
            const groupId = platformInfo.mtGroupId || platformInfo.platformGroupId;

            if (platformInfo.platform) {
                this.loading = true;
                try {
                    const response = await TournamentRepository.getWizardRankings(
                        clubId,
                        platformInfo.platform,
                        groupId
                    );
                    this.availableRankings = response.data || [];

                    // Reset ranking type if current selection is not available
                    if (this.rankingType && !this.availableRankings.find(r => r.code === this.rankingType)) {
                        this.rankingType = this.availableRankings.length > 0 ? this.availableRankings[0].code : '';
                    }
                    // Set first available ranking if none selected
                    if (!this.rankingType && this.availableRankings.length > 0) {
                        this.rankingType = this.availableRankings[0].code;
                    }
                } catch (error) {
                    /* eslint-disable no-console */
                    console.error('Error fetching available rankings:', error);
                    /* eslint-enable no-console */
                    // Fall back to default rankings
                    this.availableRankings = [];
                } finally {
                    this.loading = false;
                }
            }
        },

        validateForm() {
            // If no ranking types available, don't allow proceeding
            if (this.availableRankingTypes.length === 0) {
                this.setNextBtnDisabled(true);
                return;
            }
            const isValid = this.rankingType && this.registrationPeriod;
            this.setNextBtnDisabled(!isValid);
        }
    },
    async created() {
        this.registrationPeriods = [
            { text: this.$t('registration.periods.notAllowed'), value: 'NOT_ALLOWED' },
            { text: this.$t('registration.periods.oneDay'), value: 'ONE_DAY' },
            { text: this.$t('registration.periods.threeDays'), value: 'THREE_DAYS' },
            { text: this.$t('registration.periods.fiveDays'), value: 'FIVE_DAYS' },
            { text: this?.$t('registration.periods.always'), value: 'ALWAYS' }
        ];

        // Fetch available rankings based on platform
        await this.fetchAvailableRankings();

        // Don't set a default - let it come from API
        this.validateForm();

        // Watch for platform info changes
        this.$watch(
            () => this.$store.state.tournamentPlatformInfo,
            async () => {
                await this.fetchAvailableRankings();
            },
            { deep: true }
        );
    }
}
</script>



<style lang="scss" scoped>
.tournament-participants {
    width: 100%;

    .title {
        font-size: 32px !important;
        font-weight: bold;
        margin-bottom: 8px;
        color: var(--v-textPrimary-base);
        font-family: "Avenir", sans-serif !important;
    }

    .subtitle {
        font-size: 16px;
        color: var(--v-textSecondary-base);
        margin-bottom: 24px;
        font-family: "Avenir", sans-serif;
    }

    .info-card {
        background-color: var(--v-cardBackground-base);
        border: 1px solid #9A9A9A;
        border-radius: 10px;
        padding: 76px 60px 50px 60px;
        overflow-y: auto;

        .v-form {
            max-width: 720px;
            margin: auto;
        }

        .section {
            margin-bottom: 32px;

            .subtitle-2 {
                font-family: "Avenir", sans-serif !important;
            }

            .section-title {
                font-size: 20px;
                font-weight: 500;
                margin-bottom: 16px;
                color: var(--v-textPrimary-base);
            }

            .m-bttom{
                margin-bottom: 0px !important;
            }

            .radio-label {
                display: flex;
                flex-direction: column;
                color: var(--v-mainBlack-base);
                font-family: "Avenir", sans-serif;

                .subtitle-2 {
                    margin-top: 4px;
                    font-size: 12px;
                    font-family: "Avenir", sans-serif !important;
                }
            }
        }
    }

    ::v-deep .v-input__icon--append .v-icon {
        font-size: 14px;
        color: #9A9A9A;
    }

    ::v-deep .v-list--dense .v-list-item .v-list-item__title {
        font-size: 16px !important;
    }
}

::v-deep .v-input--switch {
    .v-input--selection-controls__input {
        .v-input--switch__track {
            height: 24px !important;
            width: 45px !important;
            top: calc(50% - 12px) !important;
            border-radius: 12px !important;
        }

        .v-input--switch__thumb {
            height: 20px !important;
            margin-left: 5px;
            width: 20px !important;
            top: calc(50% - 10px) !important;
        }
    }

    // Adjust margins for label alignment
    .v-input--selection-controls__input {
        margin-right: 18px;
    }

    // Adjust thumb position when switched on
    &.v-input--is-active {
        .v-input--switch__thumb {
            transform: translate(20px) !important;
        }

        // Add green color when active
        .v-input--switch__track {
            background-color: var(--v-mainGreen-base) !important;
            opacity: 1 !important;
        }
    }

    .v-input--selection-controls__label {
        color: var(--v-mainBlack-base) !important;
    }

    .v-label.theme--light {
        color: var(--v-mainBlack-base) !important;
    }
}

.platform-label {
    font-size: 16px;
    color: var(--v-textPrimary-base);
    font-family: "Avenir", sans-serif;
}

.timeline-container {
    padding: 20px 0;

    .timeline {
        position: relative;
        height: 100px;

        .timeline-base {
            position: absolute;
            width: 100%;
            height: 4px;
            background: #E0E0E0;
            top: 50%;
            transform: translateY(-50%);
        }

        .tournament-period {
            position: absolute;
            height: 20px;
            background: #E8F5FF;
            border: 2px solid #2196F3;
            border-radius: 20px;
            top: 50%;
            transform: translateY(-50%);
            z-index: 1;
        }

        .registration-period {
            position: absolute;
            height: 20px;
            background: #E8FFE9;
            border: 2px solid var(--v-mainGreen-base);
            border-radius: 20px;
            top: 50%;
            transform: translateY(-50%);
            z-index: 2;
        }

        .period-label {
            position: absolute;
            font-size: 12px;
            white-space: nowrap;

            &.tournament {
                top: calc(100% + 35px); // Position below timeline and dates
                left: 50%;
                transform: translateX(-50%);
                color: #2196F3;
            }

            &.registration {
                bottom: calc(100% + 4px); // Position above timeline
                left: 50%;
                transform: translateX(-50%);
                color: var(--v-mainGreen-base);
            }
        }

        .timeline-markers {
            position: absolute;
            width: 100%;
            height: 100%;
            z-index: 3; // Add this to make markers appear above everything

            .marker {
                position: absolute;
                width: 12px;
                height: 12px;
                border-radius: 50%;
                background: var(--v-cardBackground-base); // Add this to ensure marker fills are visible
                border: 2px solid #9A9A9A;
                top: 50%;
                transform: translate(-50%, -50%);
                z-index: 3; // Add this to ensure markers are above the periods

                &.start {
                    left: 0;
                    color: #9A9A9A;
                }

                &.end {
                    right: 0;
                    transform: translate(50%, -50%);
                    color: #9A9A9A;
                }

                &.registration-end {
                    left: var(--registration-end-position);
                    border-color: var(--v-mainGreen-base);
                }

                .date {
                    position: absolute;
                    top: 100%;
                    left: 50%;
                    transform: translateX(-50%);
                    margin-top: 8px;
                    font-size: 12px;
                    color: var(--v-textSecondary-base);
                    white-space: nowrap;

                    &.registration-end-date {
                        bottom: calc(100% + 25px); // Move above the timeline
                        top: auto;
                        margin-top: 0;
                        color: var(--v-mainGreen-base);
                    }
                }

                .label {
                    position: absolute;
                    top: calc(100% + 35px); // Position below the date
                    transform: translateX(-50%);
                    font-size: 11px;
                    color: #9A9A9A;
                    white-space: nowrap;
                }
            }
        }

        .registration-disabled {
            position: absolute;
            top: 50%;
            left: 0;
            transform: translateY(calc(-50% + 35px));
            color: #9A9A9A;
            font-size: 12px;
            width: 100%;
            text-align: center;
            font-style: italic;
        }

        .timeline-markers {
            // ...existing timeline-markers styles...

            .marker {
                // ...existing marker styles...

                .date {
                    // ...existing date styles...

                    &.registration-end-date {
                        left: -10px; // Add some spacing
                    }

                    &.end-date {
                        transform: translateX(0); // Align with the right edge
                        left: auto;
                        right: -10px;
                    }
                }
            }
        }
    }
}

.timeline-tooltip {
    background: var(--v-cardBackground-base) !important;
    border: 1px solid #9A9A9A;
    border-radius: 10px;
    padding: 24px 60px !important; // Increased horizontal padding
    width: 900px !important; // Slightly increased width
    max-width: none !important;

    .timeline-tooltip-content {
        h4.tooltip-title {
            color: var(--v-mainBlack-base);
            font-family: "Avenir", sans-serif !important;
            font-size: 18px;
            text-align: center;
            font-weight: bold;
            margin-bottom: 12px;
        }

        .tooltip-dates {
            color: #666666;
            text-align: center;
            font-size: 14px;
            font-family: "Avenir", sans-serif;
            margin-bottom: 24px;
        }
    }

    .timeline-container {
        margin: 0 auto;
        padding: 0;

        .timeline {
            height: 140px; // Increased height to accommodate labels
            margin: 20px 0;

            .timeline-markers {
                .marker {
                    .label {
                        // Change label position to be under the date
                        position: absolute;
                        width: 100px; // Fixed width for better alignment
                        text-align: center;
                        font-size: 11px;
                        color: #9A9A9A;
                        white-space: normal; // Allow text to wrap
                        line-height: 1.2;

                        &.start-label {
                            left: -50px; // Center align with the marker
                            top: calc(100% + 30px);
                        }

                        &.end-label {
                            right: -50px; // Center align with the marker
                            top: calc(100% + 30px);
                        }
                    }

                    .date {
                        // Adjust date spacing
                        margin-top: 12px;
                    }
                }
            }

            // Adjust period label positions
            .period-label {
                &.tournament {
                    top: calc(100% + 60px); // Move further down
                }
            }
        }
    }

    &.v-tooltip__content {
        opacity: 1 !important;
        background: var(--v-cardBackground-base) !important;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }
}

.required-chip {
    background-color: var(--v-mainGreen-base) !important;
    color: white !important;
    border: none;
    font-size: 14px;
}

.unavailable-chip {
    // ...existing unavailable-chip styles...
}

// Ranking Modal Styling
::v-deep .ranking-modal {
    .v-card {
        background: var(--v-cardBackground-base) !important;
        border-radius: 10px;
        font-family: "Avenir", sans-serif;

        .ranking-modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 24px 32px;
            border-bottom: 1px solid #e0e0e0;

            h3 {
                font-size: 24px;
                font-weight: bold;
                color: var(--v-mainBlack-base);
                margin: 0;
            }

            .v-btn {
                color: #9a9a9a;

                &:hover {
                    color: var(--v-mainBlack-base);
                }
            }
        }

        .ranking-modal-content {
            padding: 24px 32px 32px;

            .modal-description {
                font-size: 15px;
                color: var(--v-textSecondary-base);
                margin-bottom: 24px;
                line-height: 1.5;
            }

            .ranking-table-wrapper {
                overflow-x: auto;
                border: 1px solid #e0e0e0;
                border-radius: 8px;

                .ranking-comparison-table {
                    width: 100%;
                    border-collapse: collapse;
                    font-family: "Avenir", sans-serif;

                    thead {
                        background: #f5f5f5;

                        tr th {
                            padding: 16px 20px;
                            text-align: left;
                            font-weight: 600;
                            font-size: 14px;
                            color: var(--v-mainBlack-base);
                            border-bottom: 2px solid #e0e0e0;

                            &:first-child {
                                width: 20%;
                            }

                            &:nth-child(2) {
                                width: 40%;
                            }

                            &:nth-child(3) {
                                width: 40%;
                            }
                        }
                    }

                    tbody {
                        tr {
                            border-bottom: 1px solid #e0e0e0;
                            transition: background-color 0.2s;

                            &:hover {
                                background: #fafafa;
                            }

                            &:last-child {
                                border-bottom: none;
                            }

                            td {
                                padding: 16px 20px;
                                vertical-align: top;

                                &.ranking-name {
                                    font-weight: 600;
                                    color: var(--v-mainBlack-base);
                                    font-size: 14px;
                                }

                                &.ranking-description {
                                    color: var(--v-textSecondary-base);
                                    font-size: 14px;
                                    line-height: 1.5;
                                }

                                &.ranking-formula {
                                    font-family: "Courier New", monospace;
                                    font-size: 13px;
                                    color: var(--v-textPrimary-base);
                                    background: #f9f9f9;
                                    border-radius: 4px;
                                    padding: 12px 16px !important;

                                    .formula-single {
                                        white-space: pre-wrap;
                                    }

                                    .formula-dual {
                                        div {
                                            margin-bottom: 6px;

                                            &:last-child {
                                                margin-bottom: 0;
                                            }

                                            strong {
                                                color: var(--v-mainGreen-base);
                                                font-family: "Avenir", sans-serif;
                                                font-size: 12px;
                                                margin-right: 6px;
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
</style>
